import { useState, useLayoutEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import useApi from "../../../services/useApi";
import endpoints from "../../../services/endpoints";
import { convertTimestamp } from "../../../components/commonfunctions/convertDate";
import Shimmer from "../../AdminManagement/components/ViewAdminDetailsShimmer";
import useTranslation from "../../../components/CustomHooks/useTranslation";

// Extract numeric value from AFA formatted string or number
const extractNumericValue = (value: string | number | undefined | null): number => {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  // Remove "AFA" prefix and any whitespace, then extract number
  const cleaned = value.toString().replace(/AFA/gi, '').replace(/[^\d.-]/g, '').trim();
  const num = Number(cleaned);
  return isNaN(num) ? 0 : num;
};

interface TargetBreakdown {
  target_amount: string; // Now comes as "AFA 25.00"
  target_percentage: number;
}

interface CVMTargetDetails {
  transaction_id: string;
  date_and_time: string;
  sender: string;
  receiver: string;
  receiver_location: string;
  achieved_target_amount?: string;
  on_target_achievement_commission: string; // Now comes as "AFA 5.95"
  target_breakdown: TargetBreakdown[];
  total_recharge_amount?: string; // Now comes as "AFA 85.00"
  achieved_target_percentage?: string;
  cvm_period?: {
    from_date: string;
    to_date: string;
  };
}

export default function ViewCVMTargetDetails() {
  const { id } = useParams<{ id: string }>();
  const pathname = useSelector((state: RootState) => state.geolocation.previousUrl);
  const { getAPI } = useApi();
  const { cvmServiceFee } = endpoints;
  const { notEnglish } = useTranslation({});
  const [loading, setLoading] = useState(true);
  const [targetDetails, setTargetDetails] = useState<CVMTargetDetails | null>(null);
  const [error, setError] = useState<string>("");

  useLayoutEffect(() => {
    const fetchTargetDetails = async () => {
      if (!id) return;
      setLoading(true);
      setError("");
      try {
        const response = await getAPI(`${cvmServiceFee}/${id}`);
        if (!response.error && response.data?.data) {
          setTargetDetails(response.data.data);
        } else {
          setError("Failed to load target details. Please try again.");
        }
      } catch {
        setError("An error occurred while fetching target details.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTargetDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return convertTimestamp(dateString);
  };

  const previousUrl = pathname || "/zoodpay-money/cvm-service-fee";

  return (
    <div dir={notEnglish ? "rtl" : "ltr"}>
      <Breadcrumbs
        navPath={["Zoodpay Money", "CVM Service Fee", "Target Details"]}
        links={[previousUrl, previousUrl]}
        heading="Target Details"
      />

      {loading ? (
        <div className="mt-1.5 bg-white h-maintableheight rounded-lg flex flex-col">
          <div className="p-8">
            <Shimmer />
          </div>
        </div>
      ) : error ? (
        <div className="mt-1.5 bg-white h-maintableheight rounded-lg flex flex-col">
          <div className="p-8 text-center text-negative">
            <p>{error}</p>
          </div>
        </div>
      ) : targetDetails ? (
        <div className="mt-1.5 bg-white rounded-lg flex flex-col">
          {/* Transaction Information Card */}
          <div className="px-6 py-5">
            <div className="bg-blue-50 rounded-lg p-5">
              <div className="grid grid-cols-3 gap-x-8 gap-y-4">
                <div>
                  <p className="text-body2 text-primary_text mb-1">Transaction ID</p>
                  <p className="text-body1 text-primary_text">{targetDetails.transaction_id || "-"}</p>
                </div>
                <div>
                  <p className="text-body2 text-primary_text mb-1">Date and Time</p>
                  <p className="text-body1 text-primary_text">
                    {formatDate(targetDetails.date_and_time)}
                  </p>
                </div>
                <div>
                  <p className="text-body2 text-primary_text mb-1">Receiver</p>
                  <p className="text-body1 text-primary_text">
                    {targetDetails.receiver || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-body2 text-primary_text mb-1">Receiver Location</p>
                  <p className="text-body1 text-primary_text">
                    {targetDetails.receiver_location || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-body2 text-primary_text mb-1">Sender</p>
                  <p className="text-body1 text-primary_text">
                    {targetDetails.sender || "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Target Achievement Summary Cards */}
          <div className="px-6 pb-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-p_light rounded-lg p-4">
                <p className="text-heading3 text-p_primary  mb-2">Achieved Target Amount</p>
                <p className="text-heading3  text-primary_text">
                  {targetDetails.total_recharge_amount || targetDetails.achieved_target_amount || "AFA 0.00"}
                </p>
              </div>
              <div className="bg-p_light rounded-lg p-4">
                <p className="text-heading3 text-p_primary mb-2">On Target Achievement Commission</p>
                <p className="text-heading3 text-primary_text">
                  {targetDetails.on_target_achievement_commission || "AFA 0.00"}
                </p>
              </div>
            </div>
          </div>

          {/* Target Breakdown Table */}
          <div className="px-6 pb-6">
            <table className="min-w-full border border-[#D8DFE9]">
              <thead className="bg-p_light">
                <tr>
                  <th scope="col" className="px-6 py-2.5 text-left text-sm font-bold text-gray-900 tracking-wider border border-[#D8DFE9]">
                    Target
                  </th>
                  <th scope="col" className="px-6 py-2.5 text-left text-sm font-bold text-gray-900 tracking-wider border border-[#D8DFE9]">
                    Target Percentage
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {targetDetails.target_breakdown?.map((target, index) => {
                  // Calculate which target should be highlighted
                  // Highlight the highest target that is <= total_recharge_amount
                  const totalRecharge = extractNumericValue(targetDetails.total_recharge_amount);
                  const currentTarget = extractNumericValue(target.target_amount);
                  const nextTarget = targetDetails.target_breakdown[index + 1] 
                    ? extractNumericValue(targetDetails.target_breakdown[index + 1].target_amount)
                    : Infinity;
                  
                  // Check if this is the highest target that is <= total_recharge_amount
                  const isHighlighted = totalRecharge >= currentTarget && totalRecharge < nextTarget;
                  
                  return (
                    <tr
                      key={index}
                      className={
                        isHighlighted
                          ? "bg-blue-800"
                          : ""
                      }
                    >
                      <td className={`px-6 py-2.5 whitespace-nowrap text-sm font-medium border border-[#D8DFE9] ${isHighlighted ? "text-white" : "text-primary_text"}`}>
                        {target.target_amount}
                      </td>
                      <td className={`px-6 py-2.5 whitespace-nowrap text-sm font-medium border border-[#D8DFE9] ${isHighlighted ? "text-white" : "text-primary_text"}`}>
                        {target.target_percentage}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
